import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
  Icons,
} from '@ohif/ui-next';
import { ORGAN_DISPLAY_NAMES, OrganEnum, OrganQualityEnum } from '../../clients/viewer';
import { OrganRow, OrganRowState } from '../../services/OrganReviewService';
import { ANCHOR } from '../dialogAnchor';
import QualityToggle from './QualityToggle';

interface OrganReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: readonly OrganRow[];
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  hasError: boolean;
  returnFocusRef: React.RefObject<HTMLButtonElement>;
  suppressFocusReturn: React.MutableRefObject<boolean>;
  onQualityChange: (organ: OrganEnum, quality?: OrganQualityEnum) => void;
  onSubmit: () => void;
}

/**
 * What each of design D5's five row states says, and whether it takes a click.
 * Exhaustive over OrganRowState, so a sixth state fails to compile here rather than
 * rendering a blank cell.
 */
const ROW_STATES: Record<OrganRowState, { disabled: boolean; note?: string }> = {
  unissued: { disabled: true, note: 'Not available for this series' },
  gradeable: { disabled: false },
  inProgress: { disabled: true, note: 'Submitting…' },
  complete: { disabled: true },
  failed: { disabled: false, note: 'Submission failed — try again' },
};

/**
 * Rendered inline by OrganReviewButton, not through uiDialogService, for M3 D2's reason:
 * ManagedDialog.tsx:115 hardcodes `modal={false}`, so every service-raised dialog
 * forfeits Radix's focus trap.
 *
 * `modal={false}` here is DELIBERATE and is the difference from AssessmentDialog, which
 * passes `modal`. A reviewer scrolls slices with this panel open (design D3), so the trap
 * is declined rather than lost — and with it Radix's `hideOthers()` screening, which is
 * the cost.
 *
 * Escape and outside-click stay disabled, as on develop. THREE PROPS MOVED IN 3.13
 * (M3 D-6): develop passed hideOverlay / disableOutsideClick / disableEscapeKey to
 * DialogContent; in 3.13 the equivalents live on the Dialog ROOT (Dialog.tsx:21-40) and
 * reach DialogContent through context, which enforces them at :104-113. Passed to
 * DialogContent they would land on the DOM node as unknown attributes.
 */
export default function OrganReviewDialog({
  open,
  onOpenChange,
  rows,
  isSubmitDisabled,
  isSubmitting,
  hasError,
  returnFocusRef,
  suppressFocusReturn,
  onQualityChange,
  onSubmit,
}: OrganReviewDialogProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  /**
   * Plan D-14. `modal={false}` disables the focus TRAP, not the mount autofocus:
   * FocusScope's auto-focus effect (@radix-ui/react-focus-scope/dist/index.mjs:71-86) is
   * not gated by `trapped` — only the trap effect at :36 is. Without this Radix focuses
   * the first organ's role="radio", whose roving-focus group then swallows the arrow keys
   * a reviewer scrolls slices with.
   */
  const declineOpenFocus = useCallback((event: Event) => event.preventDefault(), []);

  /**
   * Plan D-14, and the same finding AssessmentDialog.tsx:104-120 records:
   * DialogContentNonModal focuses `context.triggerRef` and then preventDefault()s
   * (@radix-ui/react-dialog/dist/index.mjs:176-183), suppressing FocusScope's fallback to
   * the previously focused element — and triggerRef is populated only by DialogTrigger
   * (:63), which this panel does not render. Doing nothing strands focus on <body>.
   */
  const returnFocusToTrigger = useCallback(
    (event: Event) => {
      // Always prevented: Radix would otherwise focus a null triggerRef and strand focus
      // on <body> anyway.
      event.preventDefault();

      // But focus is only MOVED for a close the reviewer asked for. Clicking a second
      // viewport closes this panel (D-15), and yanking focus to the toolbar button there
      // is the focus theft check 7 exists to catch, arrived at from the other side.
      if (suppressFocusReturn.current) {
        suppressFocusReturn.current = false;
        return;
      }

      returnFocusRef.current?.focus();
    },
    [returnFocusRef, suppressFocusReturn]
  );

  /**
   * Guards the click rather than passing `disabled`, for the reason ToggleGroup.tsx:77-85
   * gives: a native `<button disabled>` leaves the tab order. `ActionProps.onClick` is
   * required (FooterAction.tsx:11), so the handler cannot simply be dropped.
   */
  const submitIfEnabled = useCallback(() => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  }, [isSubmitDisabled, onSubmit]);

  return (
    <Dialog
      open={open}
      modal={false}
      onOpenChange={onOpenChange}
      showOverlay={false}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      {/*
        M5.5 round 2. develop's shell border was !border-primary-main -- orange, and identical
        on its AssessmentDialog (develop:extensions/default/src/QA/components/OrganReviewDialog
        and .../Components/DiagnosisButton, byte-for-byte the same className). border-input
        measured 1.205:1 against the viewer behind it and was invisible.
      */}
      <DialogContent
        className="border-primary border sm:max-h-[300px] sm:max-w-[400px]"
        style={ANCHOR}
        onOpenAutoFocus={declineOpenFocus}
        onCloseAutoFocus={returnFocusToTrigger}
        data-cy="sycai-organ-review-dialog"
      >
        <DialogHeader>
          <DialogTitle>Organ Review</DialogTitle>
          {/*
            Radix warns when DialogContent has neither a description nor
            aria-describedby={undefined} (@radix-ui/react-dialog/dist/index.mjs:302-311).
          */}
          <DialogDescription className="sr-only">
            Grade each organ Good or Poor, then submit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-[170px] flex-col gap-2 overflow-y-auto pt-2">
          <div className="grid grid-cols-2 gap-2 border-b px-1 pb-1 text-sm font-semibold text-white">
            <div>Organ</div>
            <div className="text-center">Quality</div>
          </div>

          {/* Plan D-13: without this an errored read is indistinguishable from a series
              the server has issued nothing for -- three disabled rows either way. */}
          {hasError && (
            <div
              className="text-destructive text-xs"
              data-cy="sycai-organ-review-error"
            >
              Could not read the organ reviews for this series. Showing the last known state.
            </div>
          )}

          {rows.map(row => {
            const { disabled, note } = ROW_STATES[row.state];

            return (
              <div
                key={row.organ}
                className="grid grid-cols-2 items-center gap-2 py-1"
                data-cy={`sycai-organ-row-${row.organ.toLowerCase()}`}
                data-sycai-row-state={row.state}
              >
                <div className="text-sm font-medium text-white">
                  {ORGAN_DISPLAY_NAMES[row.organ]}
                  {note && <div className="text-muted-foreground text-xs">{note}</div>}
                </div>

                <QualityToggle
                  organ={row.organ}
                  value={row.state === 'unissued' ? undefined : row.quality}
                  onChange={quality => onQualityChange(row.organ, quality)}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>

        <FooterAction className="gap-3 pt-1 pb-2">
          {/*
            Bare close. Design D7: the Good/Poor choices survive in the service, and
            Submit is the only network call -- confirmed with the user 2026-08-12 as
            deliberate rather than the misnomer it looks like.

            M5.5 D5 kept it Secondary for the reason AssessmentDialog.tsx's own Save & Close
            records: it persists, so it is a peer action rather than the Auxiliary dismiss.

            M5.5 R-J: bg-primary/60 is develop's own value, SUPERSEDING M5.4 D8's general 40%
            on this row. The hover fill must travel with it -- once twMerge drops
            bg-secondary/85, brand.css's .bg-secondary\/85.hover\:bg-secondary\/100:hover
            compound stops matching and Tailwind's own hover:bg-secondary/100 flashes raw
            --secondary, the hue jump that compound exists to prevent.
            R-K: text on the orange accent is white, the fork's ruled convention.
          */}
          <FooterAction.Secondary
            className="bg-primary/60 hover:bg-primary/100 flex-1 text-sm hover:text-white"
            dataCY="sycai-organ-review-close"
            onClick={close}
          >
            Save &amp; Close
          </FooterAction.Secondary>

          {/*
            M5.5 round 2 R-I. develop's confirm is green, not orange. green-500 resolves
            through platform/ui/tailwind.config.js's ramp to #48BB78 -- NOT stock Tailwind's
            #22C55E, the same replaced-ramp trap red-800 carries in DeleteConfirmDialog.tsx.
            Per-surface parity only: a fork confirm action with no develop counterpart stays
            orange rather than following this one by analogy.

            R-K does NOT reach this button. It rules text on the ORANGE accent white; the label
            here is text-primary-foreground inherited from FooterAction.Primary, and white on
            green measures 2.426:1. Implementing R-K by moving --primary-foreground would flip
            it silently -- which is why R-K is call-site classes and this button carries none.
          */}
          <FooterAction.Primary
            className="flex-1 bg-green-500 text-sm hover:bg-green-500/90 aria-disabled:opacity-50"
            dataCY="sycai-organ-review-submit"
            onClick={submitIfEnabled}
            aria-disabled={isSubmitDisabled || undefined}
          >
            {isSubmitting ? (
              /* LoadingSpinner is the ONE icon that is not a bare svg spread: it bakes in
                 `h-5 w-5 animate-spin` and concatenates rather than merges
                 (Icons/Sources/LoadingSpinner.tsx:4-8), so no size class is passed here --
                 passing one would ship both and leave the result to stylesheet order. */
              <Icons.LoadingSpinner className="mr-1" />
            ) : (
              <Icons.StatusSuccess
                aria-hidden="true"
                className="mr-1 h-5 w-5"
              />
            )}
            Submit
          </FooterAction.Primary>
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
